// =============================================================================
// features/configuracoes/canais/agentConnections.ts — Status real da conexão
// dos vendedores obrigados, para o gestor cobrar (F57-S03).
//
// Fonte: `GET /api/admin/agents/whatsapp-connections` (agents:manage, escopo
// de cidade, flag `whatsapp.connection_guard.enabled`). Só agentes com
// `whatsapp_required`. Leitura com mais de 5 min vem como `UNKNOWN` — a tela
// diz "Sem leitura recente", nunca "Conectado".
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '../../../lib/api';

export const CONNECTION_GUARD_FLAG = 'whatsapp.connection_guard.enabled';

const STATUSES = [
  'WORKING',
  'STARTING',
  'SCAN_QR_CODE',
  'FAILED',
  'STOPPED',
  'NO_CHANNEL',
  'UNKNOWN',
] as const;
export type AgentConnectionStatus = (typeof STATUSES)[number];

const AgentConnectionSchema = z.object({
  agent_id: z.string().uuid(),
  display_name: z.string(),
  status: z.enum(STATUSES),
  reason: z.string().nullable(),
  since: z.string().nullable(),
  checked_at: z.string().nullable(),
  needs_action: z.boolean(),
});
export type AgentConnection = z.infer<typeof AgentConnectionSchema>;

const ListSchema = z.object({ data: z.array(AgentConnectionSchema) });

export type ConnectionTone = 'success' | 'warning' | 'danger' | 'neutral';

export const CONNECTION_STATUS_LABEL: Record<AgentConnectionStatus, string> = {
  WORKING: 'Conectado',
  STARTING: 'Conectando',
  SCAN_QR_CODE: 'Aguardando leitura do QR',
  FAILED: 'Falhou',
  STOPPED: 'Desconectado',
  NO_CHANNEL: 'Nunca conectou',
  UNKNOWN: 'Sem leitura recente',
};

/** Tom do status. `needs_action` manda: STARTING travado vira perigo. */
export function connectionTone(
  row: Pick<AgentConnection, 'status' | 'needs_action'>,
): ConnectionTone {
  if (row.status === 'WORKING') return 'success';
  if (row.needs_action) return 'danger';
  if (row.status === 'UNKNOWN') return 'neutral';
  return 'warning';
}

/** Obrigados que precisam conectar — o número do resumo. */
export function countNeedingAction(rows: readonly AgentConnection[]): number {
  return rows.filter((r) => r.needs_action).length;
}

/**
 * Ordena para cobrar: quem precisa agir primeiro, depois quem está sem leitura,
 * depois o resto; empate pelo nome (pt-BR). Não muta a entrada.
 */
export function sortForFollowUp<T extends { readonly display_name: string }>(
  items: readonly T[],
  connectionOf: (item: T) => AgentConnection | undefined,
): T[] {
  const rank = (item: T): number => {
    const row = connectionOf(item);
    if (row === undefined) return 3;
    if (row.needs_action) return 0;
    if (row.status === 'UNKNOWN') return 1;
    return 2;
  };
  return [...items].sort(
    (a, b) => rank(a) - rank(b) || a.display_name.localeCompare(b.display_name, 'pt-BR'),
  );
}

/** "desde 31/10, 09:05" no horário de Rondônia; `null` se não há data válida. */
export function formatConnectionSince(since: string | null): string | null {
  if (since === null) return null;
  const date = new Date(since);
  if (Number.isNaN(date.getTime())) return null;
  const formatted = date.toLocaleString('pt-BR', {
    timeZone: 'America/Porto_Velho',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `desde ${formatted}`;
}

/**
 * O que a linha de um vendedor pode afirmar. Enquanto a lista carrega ou se
 * ela falhou, não sabemos quem é obrigado: `unavailable` — nunca "não
 * obrigatório" (afirmar isso sem dado esconderia exatamente quem cobrar).
 */
export type AgentLineState = 'unavailable' | 'not_required' | 'tracked';

export function agentLineState(
  query: { readonly isLoading: boolean; readonly isError: boolean },
  connection: AgentConnection | undefined,
): AgentLineState {
  if (query.isLoading || query.isError) return 'unavailable';
  return connection === undefined ? 'not_required' : 'tracked';
}

export function useAgentConnections(
  enabled: boolean,
): ReturnType<typeof useQuery<AgentConnection[]>> {
  return useQuery<AgentConnection[]>({
    queryKey: ['admin-agents', 'whatsapp-connections'],
    queryFn: async () =>
      ListSchema.parse(await api.get('/api/admin/agents/whatsapp-connections')).data,
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
